import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { QesIcons } from "@app/shared/icons/qesIcons";

export enum BreadcrumbTemplateEnum {
    AccessReports,
    AccuracyEvidence,
    Admin,
    ClientAdministration,
    ClientLicenses,
    ComplianceConnect,
    Data,
    InternalReports,
    JobStatistics,
    LoginStatistics,
    ManageSpecialtyExceptionTemplates,
    ManageTemplates,
    ManageTutorials,
    MemberPopulations,
    NetworkAssessment,
    NetworkSubmissions,
    Operations,
    Overviews,
    Performance,
    ProviderSets,
    QasSoftwareReleases,
    RoleAssignments,
    SearchAddress,
    SearchCounty,
    SearchState,
    SearchZipCode,
    Tags,
    Targeting,
    Terminations,
    Tools,
    Tutorials,
    UIStatistics,
    UserAdministration,
    UserSessions,
    ViewManifest,
}

interface BreadcrumbTemplates {
    [key: string]: Breadcrumb[];
}

const adminTemplate = [{
    link: "/admin/dashboard",
    text: "Admin",
}];

const dataTemplate = [{
    iconClass: QesIcons.Database,
    text: "Data",
}];

const performanceTemplate = [{
  iconClass: QesIcons.RocketV2,
  link: "/performance/dashboard",
  text: "Performance Dashboard"
}]

const toolsTemplate = [{
    iconClass: QesIcons.ToolV2,
    text: "Tools",
}];

export const breadcrumbTemplates: BreadcrumbTemplates = {
    [BreadcrumbTemplateEnum.AccessReports]: [{
        link: "/access-reports",
        text: "Access Reports",
    }],
    [BreadcrumbTemplateEnum.AccuracyEvidence]: [
        ...toolsTemplate,
        {
            iconClass: QesIcons.SearchSm,
            text: "Accuracy Evidence"
        }
    ],
    [BreadcrumbTemplateEnum.Admin]: adminTemplate,
    [BreadcrumbTemplateEnum.ClientAdministration]: [
        ...adminTemplate,
        {
            link: "/admin/client-administration",
            text: "Client Administration",
        },
    ],
    [BreadcrumbTemplateEnum.ClientLicenses]: [
        ...adminTemplate,
        {
            iconClass: QesIcons.FileV4,
            link: "/admin/client-license/manager",
            text: "Client Licenses",
        },
    ],
    [BreadcrumbTemplateEnum.ComplianceConnect]: [{
        link: "/compliance-connect",
        text: "Compliance Connect",
    }],
    [BreadcrumbTemplateEnum.Data]: dataTemplate,
    [BreadcrumbTemplateEnum.InternalReports]: [
        ...adminTemplate,
        {
            iconClass: QesIcons.Folder,
            link: "/admin/internal-reports",
            text: "Internal Reports",
        },
    ],
    [BreadcrumbTemplateEnum.JobStatistics]: [
        ...performanceTemplate,
        {
          iconClass: QesIcons.TerminalBrowser,
          text: "Job Statistics"
        }
    ],
    [BreadcrumbTemplateEnum.LoginStatistics]: [
      ...adminTemplate,
      {
        iconClass: QesIcons.LogInV1,
        text: "Login Statistics"
      }
    ],
    [BreadcrumbTemplateEnum.ManageSpecialtyExceptionTemplates]: [
        ...adminTemplate,
        { text: "Manage Specialty Exception Templates" }
    ],
    [BreadcrumbTemplateEnum.ManageTemplates]: [
        ...adminTemplate,
        {
            iconClass: QesIcons.Folder,
            link: "/admin/manage-templates",
            text: "Manage Templates",
        }
    ],
    [BreadcrumbTemplateEnum.ManageTutorials]: [
        ...adminTemplate,
        {
          iconClass: QesIcons.PencilV1,
          text: "Manage Tutorials",
        }
    ],
    [BreadcrumbTemplateEnum.MemberPopulations]: [
        ...dataTemplate,
        {
            iconClass: QesIcons.UsersV1,
            link: "/member-populations",
            text: "Member Populations",
        },
    ],
    [BreadcrumbTemplateEnum.NetworkAssessment]: [{
        link: "/?Filter=NetworkAssessment",
        text: "Network Assessment",
    }],
    [BreadcrumbTemplateEnum.NetworkSubmissions]: [{
        link: "/network-submission",
        text: "Network Submissions",
    }],
    [BreadcrumbTemplateEnum.Operations]: [{
        link: "/operations/dashboard",
        text: "Operations",
    }],
    [BreadcrumbTemplateEnum.Overviews]: [{
        text: "Overviews",
    }],
    [BreadcrumbTemplateEnum.Performance]: performanceTemplate,
    [BreadcrumbTemplateEnum.ProviderSets]: [
        ...dataTemplate,
        {
            iconClass: QesIcons.UserV1,
            link: "/provider-sets/manager",
            text: "Providers",
        },
    ],
    [BreadcrumbTemplateEnum.QasSoftwareReleases]: [
        ...adminTemplate,
        {
            link: "/admin/software-releases",
            text: "QAS Software Releases",
        }
    ],
    [BreadcrumbTemplateEnum.RoleAssignments]: [{
          iconClass: QesIcons.List,
          text: "Role Assignments",
    }],
    [BreadcrumbTemplateEnum.SearchAddress]: [
      ...toolsTemplate,
      {
        iconClass: QesIcons.MarkerPinV1,
        text: "Lookup an Address",
      }
    ],
    [BreadcrumbTemplateEnum.SearchCounty]: [
      ...toolsTemplate,
      {
        iconClass: QesIcons.SearchSm,
        text: "Lookup a County",
      }
    ],
    [BreadcrumbTemplateEnum.SearchState]: [
      ...toolsTemplate,
      {
        iconClass: QesIcons.SearchSm,
        text: "Lookup a State",
      }
    ],
    [BreadcrumbTemplateEnum.SearchZipCode]: [
      ...toolsTemplate,
      {
        iconClass: QesIcons.SearchSm,
        text: "Lookup a Zip Code",
      }
    ],
    [BreadcrumbTemplateEnum.Tags]: [{
        link: "/tags/view",
        text: "Tag Management",
    }],
    [BreadcrumbTemplateEnum.Targeting]: [{
        text: "Volume Targeting",
    }],
    [BreadcrumbTemplateEnum.Terminations]: [{
        text: "Volume Termination",
    }],
    [BreadcrumbTemplateEnum.Tools]: toolsTemplate,
    [BreadcrumbTemplateEnum.Tutorials]: [
        { text: "Help" },
        {
            iconClass: QesIcons.PencilV1,
            text: "Tutorials",
        }
    ],
    [BreadcrumbTemplateEnum.UIStatistics]: [
        ...performanceTemplate,
        { text: "UI Statistics" }
    ],
    [BreadcrumbTemplateEnum.UserAdministration]: [
        ...adminTemplate,
        {
            iconClass: QesIcons.UserV1,
            link: "/admin/user-administration",
            text: "User Administration",
        }
    ],
    [BreadcrumbTemplateEnum.UserSessions]: [
        ...adminTemplate,
        {  text: "User Sessions" }
    ],
    [BreadcrumbTemplateEnum.ViewManifest]: [
        ...adminTemplate,
        { text: "View Manifest" }
    ],
};
